import { ref } from 'vue';
import { useSdk } from '@/composables/use-sdk';
import type { PresenceStatusDefinition } from '@/sdk/model';

// Shared between every caller (#753): a presence status's label, colour and order are server
// data editable from presence-status-tab.vue (the administration screen), not a table frozen in
// the frontend — a single load per session is enough, for the same reason as
// use-calendar-lookup and use-schedule-pattern-lookup.
const definitions = ref<PresenceStatusDefinition[]>([]);
const loaded = ref(false);
let loadPromise: Promise<void> | null = null;

/**
 * Loads the list of presence status definitions once and caches it at module level. Every chart
 * concerned calls this composable on initialization to kick off the shared load (same recipe as
 * useCalendarLookup / useSchedulePatternLookup); the reading itself goes through
 * {@link findPresenceStatusDefinition}, consulted from `utils/presence-status-display.ts` while
 * a `computed` evaluates — the dependency on `definitions` is then tracked normally, so that
 * `computed` re-evaluates once the load is finished.
 */
export function usePresenceStatusLookup() {
  const sdk = useSdk();

  function load(): Promise<void> {
    if (loadPromise) return loadPromise;
    loadPromise = (async () => {
      try {
        const res = await sdk.getPresenceStatusDefinitions();
        definitions.value = res.definitions ?? [];
      } catch (e) {
        console.error('Erreur lors du chargement des définitions de statut de présence', e);
        definitions.value = [];
        // Do not memoize the failure: without this reset, a transient (network) failure would
        // condemn the whole session to the hard-coded fallback alone, even once connectivity is
        // back (same recipe as use-schedule-pattern-lookup).
        loadPromise = null;
      } finally {
        loaded.value = true;
      }
    })();
    return loadPromise;
  }

  void load();

  return {
    definitions,
    loaded,
    /** Forces a reload — after a change made from presence-status-tab.vue. */
    reload: () => {
      loadPromise = null;
      loaded.value = false;
      return load();
    },
  };
}

/**
 * Synchronous, non-triggering read of a status definition by its key — it never loads anything
 * itself (see {@link usePresenceStatusLookup}): before the first load, or if no caller has
 * mounted the composable, it returns `undefined` and the caller falls back on its own default.
 */
export function findPresenceStatusDefinition(key: string): PresenceStatusDefinition | undefined {
  return definitions.value.find(d => d.key === key);
}
